import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Message } from "../../types";
import { ChatAvatar } from "./ChatAvatar.tsx";
import { SourceList } from "./SourceList.tsx";
import { ActionButtons } from "./ActionButtons.tsx";
import { FileTextIcon } from "./Icons.tsx";

interface ChatMessageProps {
  message: Message;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isAi = message.role === "ai";

  return (
    <div className={`flex gap-4 sm:gap-6 group ${isAi ? "flex-col sm:flex-row" : "flex-row-reverse"}`}>
      <ChatAvatar isAi={isAi} />

      {/* Message Bubble */}
      <div className={`max-w-[92%] sm:max-w-[85%] lg:max-w-[80%] rounded-3xl p-5 sm:p-7 relative transition-all ${
        isAi
          ? "glass-panel bg-white/40 dark:bg-slate-900/40"
          : "bg-blue-600 text-white shadow-xl border border-blue-500/30"
      }`}
      >
        {message.file && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs mb-3 w-fit ${
            isAi
              ? "bg-slate-50 dark:bg-slate-900 border-slate-200/50 dark:border-slate-800 text-slate-600 dark:text-slate-300"
              : "bg-blue-700/50 border-blue-500/50 text-blue-100"
          }`}
          >
            <FileTextIcon className="w-4 h-4 shrink-0" />
            <span className="font-semibold truncate max-w-[180px]">{message.file.name}</span>
          </div>
        )}

        <div className={`max-w-none text-[1.05rem] leading-relaxed selection:bg-blue-200 dark:selection:bg-blue-800/50 ${
          isAi
            ? "prose dark:prose-invert text-slate-800 dark:text-slate-200 prose-p:leading-relaxed prose-p:mb-5 prose-li:my-2 prose-pre:bg-slate-900 prose-pre:border prose-pre:border-slate-800 prose-headings:text-slate-900 dark:prose-headings:text-white prose-strong:text-blue-600 dark:prose-strong:text-blue-400 prose-a:text-blue-600 dark:prose-a:text-blue-400"
            : "text-white whitespace-pre-wrap font-medium"
        }`}
        >
          {isAi
            ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {message.content}
                </ReactMarkdown>
              )
            : (
                message.content
              )}
        </div>

        {isAi && <ActionButtons content={message.content} sources={message.sources} />}

        {isAi && message.sources && (
          <SourceList sources={message.sources} />
        )}
      </div>
    </div>
  );
};
